(() => {
  if (customElements.get('kr-all-rugs-infinite-v2')) return;

  class KeshRugAllRugsInfiniteV2 extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.gridId = this.dataset.gridId;
      this.sectionId = this.dataset.sectionId;
      this.nextUrl = this.dataset.nextUrl || '';
      this.grid = document.getElementById(this.gridId);
      this.status = this.querySelector('.kr-all-rugs-grid-v2__loader-status');
      this.retry = this.querySelector('.kr-all-rugs-grid-v2__retry');
      this.loading = false;

      this.retry?.addEventListener('click', () => this.loadNext());

      if (!this.grid || !this.nextUrl) {
        this.finish();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        if (this.retry) this.retry.hidden = false;
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
        },
        { rootMargin: '900px 0px', threshold: 0 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async loadNext() {
      if (this.loading || !this.nextUrl || !this.grid) return;

      this.loading = true;
      this.classList.add('is-loading');
      this.grid.setAttribute('aria-busy', 'true');
      if (this.retry) this.retry.hidden = true;
      if (this.status) this.status.textContent = this.dataset.loadingLabel || 'Loading more rugs';

      try {
        const requestUrl = new URL(this.nextUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', this.sectionId);
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        });

        if (!response.ok) throw new Error(`Unable to load page: ${response.status}`);

        const sourceDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sourceGrid = sourceDocument.getElementById(this.gridId);
        if (!sourceGrid) throw new Error('The next product grid was not found');

        const existingUrls = new Set(
          Array.from(this.grid.querySelectorAll('[data-kr-all-rugs-product] a[href]'))
            .map((link) => link.getAttribute('href'))
        );
        const fragment = document.createDocumentFragment();
        let added = 0;

        sourceGrid.querySelectorAll('[data-kr-all-rugs-product]').forEach((item) => {
          const productUrl = item.querySelector('a[href]')?.getAttribute('href');
          if (!productUrl || existingUrls.has(productUrl)) return;
          existingUrls.add(productUrl);
          const newItem = document.importNode(item, true);
          newItem.classList.add('kr-all-rugs-grid-v2__item--revealed');
          fragment.appendChild(newItem);
          added += 1;
        });

        this.grid.appendChild(fragment);

        const sourceLoader = sourceGrid.parentElement?.querySelector('kr-all-rugs-infinite-v2');
        this.nextUrl = sourceLoader?.dataset.nextUrl || '';
        this.dataset.nextUrl = this.nextUrl;

        if (this.status && added > 0) {
          this.status.textContent = `${added} ${this.dataset.loadedLabel || 'more rugs loaded'}`;
        }

        if (!this.nextUrl) {
          this.finish();
        } else if (added === 0) {
          window.setTimeout(() => this.loadNext(), 80);
        }
      } catch (error) {
        if (this.status) this.status.textContent = this.dataset.errorLabel || 'More rugs could not be loaded';
        if (this.retry) this.retry.hidden = false;
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        this.grid.removeAttribute('aria-busy');
      }
    }

    finish() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.observer?.disconnect();
      this.classList.add('is-complete');
      if (this.retry) this.retry.hidden = true;
      if (this.status) this.status.textContent = this.dataset.completeLabel || 'All rugs are shown';
    }
  }

  customElements.define('kr-all-rugs-infinite-v2', KeshRugAllRugsInfiniteV2);
})();
